import { useCallback, useEffect } from 'react';

const textInputTypes = ['text', 'email', 'url', 'password', 'search', 'tel'];

/**
 * Workaround for fixed bar not sticking to the bottom of the screen in some cases on Firefox Mobile.
 * Reproduce the bug: on scrollable pages when an input is focused and the keyboard panel is visible,
 * try scrolling the page just a bit so that the URL bar disappears but the keyboard is still up.
 * In this situation when the user taps outside the input, the fixed bar does not slide back to the bottom completely.
 * This is a bug in Firefox unfortunately. The workaround adds a touchstart event listener to the window
 * and checks if the currently focused element is a text input of some kind. If so, tapping outside
 * immediately closes the keyboard panel so that the above mentioned bug cannot occur.
 * The fixed bar is excluded from the touch detection because otherwise its buttons wouldn't be clickable.
 */
export const useFFMobileBottomBarWorkaround = (portalId: string): void => {
  const touchHandler = useCallback((event: TouchEvent) => {
    const activeEl = document.activeElement;
    if (event.target === activeEl) return;
    const isBottomBar = document.getElementById(portalId)?.contains(event.target as HTMLElement) ?? false;
    const isActiveElementTextInput = activeEl instanceof HTMLInputElement && textInputTypes.includes(activeEl.type);
    if (!isBottomBar && isActiveElementTextInput) activeEl.blur();
  }, []);

  useEffect(() => {
    // Check if browser is Firefox and if it supports touch events
    if (/Firefox/i.test(navigator.userAgent) && ('ontouchstart' in window || navigator.maxTouchPoints > 0)) {
      window.addEventListener('touchstart', touchHandler);
    }

    return () => {
      window.removeEventListener('touchstart', touchHandler);
    };
  }, []);
};
